import axios from 'axios';

const SW_URL = '/service-worker.js';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
}

export function pushPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function ensureServiceWorker() {
  if (!isPushSupported()) return null;
  // Always register — browsers dedupe identical registrations
  try {
    return await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  } catch (err) {
    console.warn('SW registration failed', err);
    return null;
  }
}

/** Ask the user for permission and register a Web Push subscription with the backend.
 *  Returns { ok: true } on success, or { ok: false, reason, detail? } on failure. */
export async function subscribeToPush({ token, language = 'es' } = {}) {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };

  // iOS Safari only allows Web Push when the site is launched as a
  // Home-Screen PWA (standalone display mode). Detect & guide the user.
  try {
    const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isiOS && !standalone) {
      return { ok: false, reason: 'ios-not-pwa' };
    }
  } catch (_) { /* no-op */ }

  let reg;
  try {
    reg = await ensureServiceWorker();
  } catch (err) {
    return { ok: false, reason: 'sw-failed', detail: String(err?.message || err) };
  }
  if (!reg) return { ok: false, reason: 'sw-failed' };

  // Ask permission
  let perm;
  try {
    perm = await Notification.requestPermission();
  } catch (err) {
    return { ok: false, reason: 'permission-error', detail: String(err?.message || err) };
  }
  if (perm !== 'granted') return { ok: false, reason: 'denied' };

  // Get VAPID public key
  const base = process.env.REACT_APP_BACKEND_URL;
  let vapid;
  try {
    const res = await axios.get(`${base}/api/notifications/vapid-key`);
    vapid = res.data;
  } catch (err) {
    return { ok: false, reason: 'vapid-fetch-failed', detail: String(err?.message || err) };
  }
  if (!vapid?.key) return { ok: false, reason: 'no-vapid' };

  // Subscribe
  let sub;
  try {
    sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.key),
      });
    }
  } catch (err) {
    return { ok: false, reason: 'subscribe-failed', detail: String(err?.message || err) };
  }

  // Send to backend
  try {
    await axios.post(`${base}/api/notifications/subscribe`, {
      endpoint: sub.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh')))),
        auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth')))),
      },
      language,
    }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (err) {
    return { ok: false, reason: 'backend-register-failed', detail: String(err?.response?.data?.detail || err?.message || err) };
  }

  return { ok: true };
}

/** Best-effort unsubscribe locally + on backend. */
export async function unsubscribeFromPush({ token } = {}) {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch (_) { /* no-op */ }
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    await axios.post(`${base}/api/notifications/unsubscribe`, { endpoint }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (_) { /* no-op */ }
}
